import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@core/services/core/auth/auth.service';
import { ContextService } from '@core/services/core/context.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { NavigationHelper } from '@core/services/core/navigation-helper.service';
import { OperationRegisterService } from '@core/services/core/operation-register.service';
import { PoliciesHelperService } from '@core/services/core/policies-helper.service';
import { UiHelperService } from '@core/services/core/ui-helper.service';
import { AddressHelperService } from '@core/services/helpers/address-helper.service';
import { ClientAreaHelperService } from '@core/services/helpers/client-area-helper.service';
import { GeocoderHelperService } from '@core/services/helpers/geocoder-helper.service';
import { OpinatorHelperService } from '@core/services/helpers/opinator-helper.service';
import { UrlHelper } from '@core/services/helpers/url-helper.service';
import { ProfileService } from '@core/services/http-services/profile.service';
import { RoadAssistanceService } from '@core/services/http-services/road-assistance.service';
import { Address } from '@models/address';
import { AllFeatureFlags } from '@models/mmobile';
import { RoadAssistanceOperation } from '@models/operation-register';
import { PolicyCar } from '@models/policy-car';
import { ProfileUserInfo } from '@models/profile';
import {
  RoadAssistanceAddress, RoadAssistanceContextState, RoadAssistanceMessage,
  RoadAssistanceMessageType, RoadAssistanceTemplateConfiguration, RoadAssistanceUrls, RoadAssistanceValidationCodes,
  RoadAssistanceValidationStrings
} from '@modules/road-assistance/models/road-assistance';
import { TranslateService } from '@ngx-translate/core';
import { ValidatorNonEmptyMobile, ValidatorTrim } from '@utils/custom-validators';
import { spinner } from '@utils/rxjs';
import { StoreUtils } from '@utils/store';
import { capitalize } from '@utils/utils';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class RoadAssistanceContextService extends StoreUtils<RoadAssistanceContextState>{

  constructor(
    private profileService: ProfileService,
    private ui: UiHelperService,
    private clientAreaHelper: ClientAreaHelperService,
    private policiesHelper: PoliciesHelperService,
    private nav: NavigationHelper,
    private translate: TranslateService,
    public ctx: ContextService,
    private or: OperationRegisterService,
    private geoHelperService: GeocoderHelperService,
    public roadAssistanceService: RoadAssistanceService,
    private mmobileHelper: MMobileHelperService,
    private addressHelper: AddressHelperService,
    private urlHelper: UrlHelper,
    private auth: AuthService,
    private opinatorHelper: OpinatorHelperService
  ) {
    super();
  }

  initConfirmDataForm(state: Partial<RoadAssistanceContextState>): FormGroup {
    let confirmDataForm = {
      name: new FormControl('', Validators.required),
      phone: new FormControl('', [Validators.required, ValidatorNonEmptyMobile]),
      documentNumber: new FormControl('', [])
    };
    if (!this.isOnlineCase(state)) {
      const confirmDataFormOffline = {
        street: new FormControl('', [Validators.required, ValidatorTrim]),
        number: new FormControl('', [Validators.required, ValidatorTrim])
      };
      confirmDataForm = { ...confirmDataForm, ...confirmDataFormOffline };
    }
    return new FormGroup(confirmDataForm);
  }

  initAdditionalCommentForm(): FormGroup {
    return new FormGroup({
      street: new FormControl('', [Validators.required, ValidatorTrim]),
      number: new FormControl('', [Validators.required, ValidatorTrim])
    });
  }

  initNumberOfOccupantsForm() {
    return new FormGroup({
      numberOfOccupants: new FormControl('', Validators.required),
    });
  }

  async initializeConfirmDataForm(formContact, state?: Partial<RoadAssistanceContextState>) {
    const user: ProfileUserInfo = await this.profileService.getUserInfo()
      .pipe(spinner('initialize-confirm-data-form'))
      .toPromise()
      .catch(() => null);

    if (user) {
      const name = user.name || '';
      const surname1 = user.lastname || '';
      const surname2 = user.lastname2 || '';

      formContact.patchValue({
        phone: user.phones[0],
        name: `${name} ${surname1} ${surname2}`,
        documentNumber: user.documentNumber
      });
    }
    if (!this.isOnlineCase(state)) {
      formContact.patchValue({
        street: state.assistanceAddress?.addressData?.street,
        number: state.assistanceAddress?.addressData?.number
      });
    }

    return formContact;
  }

  initializeAdditionalCommentForm(formContact, state?: Partial<RoadAssistanceContextState>) {
    formContact.patchValue({
      street: state.assistanceAddress?.addressData?.street,
      number: state.assistanceAddress?.addressData?.number
    });

    return formContact;
  }

  // NAV TO CONTINUE

  navToUrlFromProblemType(state: Partial<RoadAssistanceContextState>) {
    const navigationOptions = {
      BREAKDOWN: () => {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.BREAKDOWN_SELECTED, state.policySelected.frameNumber);
        this.navToContactDataFromProblemType(state);
      },
      BATTERY: () => {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.BATTERY_SELECTED, state.policySelected.frameNumber);
        this.navToContactDataFromProblemType(state);
      },
      ACCIDENT: () => {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.ACCIDENT_SELECTED, state.policySelected.frameNumber);
        this.updatedStateOnNav(state, RoadAssistanceUrls.INFO, {
          progressBarPercentageDecimal: 0.5
        });
      },
      BLOWOUT: () => {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.BLOWOUT_SELECTED, state.policySelected.frameNumber);
        this.navToMapFromProblemType(state);
      },
      FUEL: () => {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.FUEL_SELECTED, state.policySelected.frameNumber);
        this.navToMapFromProblemType(state);
      },
      OTHER: () => {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.OTHER_SELECTED, state.policySelected.frameNumber);
        this.navToMapFromProblemType(state);
      },
      DEFERRED: () => {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.DEFERRED_SELECTED, state.policySelected.frameNumber);
        this.navToMapFromProblemType(state);
      }
    };
    return navigationOptions[state.problemSelected.type]();
  }

  navFromMapOnContinue(state: Partial<RoadAssistanceContextState>) {
    const flow = state.problemSelected?.type ? state.problemSelected?.type : 'OFFLINE';

    const navigationOptions = {
      BREAKDOWN: () => this.navToAdditionalComment(state),
      BATTERY: () => this.navToAdditionalComment(state),
      ACCIDENT: () => this.navToContactData(state),
      BLOWOUT: () => this.navToContactData(state),
      FUEL: () => this.navToContactData(state),
      OTHER: () => this.navToContactData(state),
      DEFERRED: () => this.navToContactData(state),
      OFFLINE: () => this.navToContactData(state)
    };
    return navigationOptions[flow]();
  }

  navFromContactDataOnContinue(state: Partial<RoadAssistanceContextState>) {
    const navigationOptions = {
      BREAKDOWN: () => this.navToUndergroundParking(state),
      BATTERY: () => this.navToUndergroundParking(state),
      ACCIDENT: () => this.generateRoadAssistanceOnline(state),
      BLOWOUT: () => this.generateRoadAssistanceOnline(state),
      FUEL: () => this.generateRoadAssistanceOnline(state),
      OTHER: () => this.generateRoadAssistanceOnline(state),
      DEFERRED: () => this.generateRoadAssistanceOnline(state)
    };
    return navigationOptions[state.problemSelected.type]();
  }

  navFromTravelersOnContinue(state: Partial<RoadAssistanceContextState>) {
    const navigationOptions = {
      BREAKDOWN: () => this.navFromTravelersCheck(state),
      BATTERY: () => this.updatedStateOnNav(state, RoadAssistanceUrls.MAP, {
        progressBarPercentageDecimal: 0.8,
        hideCallButton: true
      }),
    };
    return navigationOptions[state.problemSelected.type]();
  }

  // NAV TO BACK

  navFromMapOnBack(state: Partial<RoadAssistanceContextState>) {
    const flow = state.problemSelected?.type ? state.problemSelected?.type : 'OFFLINE';
    const navigationOptions = {
      BREAKDOWN: () => this.navFromMapOnBackWithOccupants(state),
      BATTERY: () => this.updatedStateOnNav(state, RoadAssistanceUrls.NUM_TRAVELERS, {
        progressBarPercentageDecimal: 0.7,
      }),
      ACCIDENT: () => this.navToInfoPage(state),
      BLOWOUT: () => this.navToSelectProblem(state),
      FUEL: () => this.navToSelectProblem(state),
      OTHER: () => this.navToSelectProblem(state),
      DEFERRED: () => this.navToSelectProblem(state),
      OFFLINE: () => this.navToInfoPage(state)
    };
    return navigationOptions[flow]();
  }

  navFromContactDataOnBack(state: Partial<RoadAssistanceContextState>) {
    const flow = state.problemSelected?.type ? state.problemSelected?.type : 'OFFLINE';
    const navigationOptions = {
      BREAKDOWN: () => this.navToSelectProblem(state),
      BATTERY: () => this.navToSelectProblem(state),
      ACCIDENT: () => this.navToMapBackContactData(state),
      BLOWOUT: () => this.navToMapBackContactData(state),
      FUEL: () => this.navToMapBackContactData(state),
      OTHER: () => this.navToMapBackContactData(state),
      DEFERRED: () => this.navToMapBackContactData(state),
      OFFLINE: () => this.updatedStateOnNav(state, RoadAssistanceUrls.MAP, {
        hideProgressBar: true,
        hideCallButton: false,
        showSendButton: true
      })
    };
    return navigationOptions[flow]();
  }

  navFromEditAddessBack(state: Partial<RoadAssistanceContextState>) {
    const flow = state.problemSelected?.type ? state.problemSelected?.type : 'OFFLINE';
    const navigationOptions = {
      BREAKDOWN: () => this.navToAdditionalComment(state),
      BATTERY: () => this.navToAdditionalComment(state),
      ACCIDENT: () => this.navToContactData(state),
      BLOWOUT: () => this.navToContactData(state),
      FUEL: () => this.navToContactData(state),
      OTHER: () => this.navToContactData(state),
      DEFERRED: () => this.navToContactData(state),
      OFFLINE: () => this.navToContactData(state)
    };
    return navigationOptions[flow]();
  }

  navFromAdditionalCommentBack(state: Partial<RoadAssistanceContextState>) {
    const navigationOptions = {
      BREAKDOWN: () => this.navToAdditionalComment(state),
      BATTERY: () => this.navToMap(state)
    };
    return navigationOptions[state.problemSelected.type]();
  }

  //

  updatedStateOnNav(
    state: Partial<RoadAssistanceContextState>,
    url: RoadAssistanceUrls,
    templateCongig?: RoadAssistanceTemplateConfiguration) {
    state.templateConfiguration = {} as RoadAssistanceTemplateConfiguration;
    state.templateConfiguration.progressBarPercentageDecimal = templateCongig?.progressBarPercentageDecimal;
    state.templateConfiguration.hideProgressBar = templateCongig?.hideProgressBar;
    state.templateConfiguration.hideCallButton = templateCongig?.hideCallButton;
    state.templateConfiguration.showAddessInput = templateCongig?.showAddessInput;
    state.templateConfiguration.showSendButton = templateCongig?.showSendButton;

    this.updateState({ ...state });
    this.nav.toNew(url);
  }

  checkRoadAssistanceAvailable(state: Partial<RoadAssistanceContextState>): boolean {
    let available = true;
    const currentDay = moment();
    const weekDay = currentDay.isoWeekday();

    const elevenPM = moment(`${currentDay.format('YYYY-MM-DD')} 11:00 PM`);
    const elevenHalfPastPM = moment(`${currentDay.format('YYYY-MM-DD')} 11:30 PM`);
    const fourHalfPastAM = moment(`${currentDay.format('YYYY-MM-DD')} 04:30 AM`);

    const mondayAvailable = (weekDay === 1 && currentDay.isAfter(elevenPM));
    const tuesdayAvailable = (weekDay === 2 && (currentDay.isBefore(fourHalfPastAM) || currentDay.isAfter(elevenHalfPastPM)));
    const wednesdayAvailable = (weekDay === 3 && (currentDay.isBefore(fourHalfPastAM) || currentDay.isAfter(elevenPM)));
    const thursdayAvailable = (weekDay === 4 && currentDay.isBefore(fourHalfPastAM));
    const sundayAvailable = (weekDay === 7 && currentDay.isBefore(fourHalfPastAM));

    const mondayTuesdayAvailable = mondayAvailable || tuesdayAvailable;
    const wednesdayThursdayAvailable = wednesdayAvailable || thursdayAvailable;

    if (mondayTuesdayAvailable || wednesdayThursdayAvailable || sundayAvailable) {
      this.or.regiesterRoadAssistance(RoadAssistanceOperation.CALL_RESTRICTIONS, state?.policySelected?.frameNumber || null);
      available = false;
    }

    return available;
  }

  async initializeRoadAssistanceFlow(policySelected?: PolicyCar) {
    this.resetState();
    const state: Partial<RoadAssistanceContextState> = {};

    const carPolicies = await this.policiesHelper.getPoliciesByPolicyBaseType('car')
      .pipe(spinner('get-car-policies'))
      .toPromise()
      .catch(() => []);

    let onlineFlowAccess = true;

    if (policySelected || carPolicies.length === 1) {
      state.policySelected = policySelected ? policySelected : carPolicies[0];
      onlineFlowAccess = this.checkUserAvailableOnlineFlow();
      if (onlineFlowAccess) {
        const vehicleValid = await this.vehicleValid(state).catch(e => false);
        onlineFlowAccess = vehicleValid;
      }
    }

    state.fromPolicyDetail = policySelected ? true : false;
    const enabledRoadAssistanceAPP = this.mmobileHelper.checkIsFeatureEnabled(AllFeatureFlags.ROAD_ASSISTANCE_APP);

    this.or.regiesterRoadAssistance(RoadAssistanceOperation.ACCESS, null,
      state.fromPolicyDetail ? 'Acceso Producto' : 'Acceso Menú');

    if ( enabledRoadAssistanceAPP && onlineFlowAccess) {
      if (this.checkRoadAssistanceAvailable(state)) {
        this.checkInitialFlowPage(carPolicies, policySelected, state);
      } else {
        this.initializeRoadAssistanceOfflineFlow(state);
      }
    } else {
      if (carPolicies.length === 1 || policySelected) {
        this.updatedStateOnNav(state, RoadAssistanceUrls.INFO, {
          progressBarPercentageDecimal: 0.5
        });
      } else {
        this.updatedStateOnNav({}, RoadAssistanceUrls.SELECT_POLICY);
      }
    }
  }

  initializeRoadAssistanceOfflineFlow(state: Partial<RoadAssistanceContextState>) {
    state.problemSelected.type = 'OFFLINE';
    state.problemSelected.code = 0;
    this.navToInfoPage(state);
  }

  checkUserAvailableOnlineFlow(): boolean {
    const { user } = this.auth.getState();
    const priorityAccess = this.mmobileHelper.getCustomConfigData('ROAD_ASSISTANCE', 'priorityAccess');

    const masc = priorityAccess.find(access => access.type === 'MASC' && access.active);
    if (masc) {
      return user.documentNumber.match(masc.condition)?.length > 0;
    }
    const dni = priorityAccess.find(access => access.type === 'DNI' && access.active);
    if (dni) {
      return dni.condition.includes(user.documentNumber);
    }
    return true;
  }

  private checkInitialFlowPage(carPolicies: any[], policySelected: PolicyCar, state: Partial<RoadAssistanceContextState>) {
    if (carPolicies.length === 1 || policySelected) {
      this.updatedStateOnNav(state, RoadAssistanceUrls.SELECT_PROBLEM);
    } else {
      this.updatedStateOnNav({}, RoadAssistanceUrls.SELECT_POLICY);
    }
  }

  openCancelProcessModal(state: Partial<RoadAssistanceContextState>): void {
    const path = 'CANCEL_PROCESS_MODAL';
    const title = this.translate.instant(`${path}.TITLE`);
    const message = this.translate.instant(`${path}.MESSAGE`);
    const okBTnTxt = this.translate.instant('LEAVE');
    const onSuccess = this.processAbandonment(state);
    this.ui.presentDialog(title, message, onSuccess, null, okBTnTxt);
  }

  processAbandonment(state: Partial<RoadAssistanceContextState>) {
    return state.fromPolicyDetail ?
      () => {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.ABANDON, state.policySelected.frameNumber);
        this.completeState();
        this.clientAreaHelper.navToPolicyDetail(state.policySelected);
      }
      : () => {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.ABANDON, state.policySelected.frameNumber);
        this.completeState();
        this.nav.goHomePage();
      };
  }

  openMaxIssuesModal(state: Partial<RoadAssistanceContextState>) {
    this.ui.presentDialogOneButton(
      this.translate.instant('ROAD_ASSISTANCE.MAX_ISSUES_MODAL.TITLE'),
      this.translate.instant('ROAD_ASSISTANCE.MAX_ISSUES_MODAL.DESCRIPTION'),
      () => {
        state.problemSelected.type = 'OFFLINE';
        state.problemSelected.code = 0;
        this.updatedStateOnNav(state, RoadAssistanceUrls.MAP, {
          progressBarPercentageDecimal: 0.5,
          showSendButton: true,
          maxIssues: true
        });
      },
      this.translate.instant('UNDERSTOOD')
    );
  }

  transformStateToRequestBody(state: Partial<RoadAssistanceContextState>) {
    return {
      cause: state.problemSelected.code,
      assistanceContact: {
        contactMethods: [
          {
            contactPhone: state.assistanceData.contactData.phone
          }
        ]
      },
      asistanceDate: moment().format('YYYY-MM-DD'),
      assistanceLocation: {
        address: state.assistanceAddress?.fullAddress,
        addressAdditionalInfo: state.assistanceAddress?.rawAddress,
        latitudeCoordinate: state.assistanceAddress?.lat,
        longitudeCoordinate: state.assistanceAddress?.lng,
        provinceCode: state.assistanceAddress?.postalCode.slice(0, 2)
      },
      vehicleInGarage: state.assistanceData.isParkedUnderground || false,
      occupantsNumber: (state.assistanceData.numberOfOccupants || 0) + 1,

      policyId: state.policySelected.policyNumber,
      taxiRequested: state.assistanceData.needTransportService || false,
      plate: state.policySelected.frameNumber
    };
  }

  generateRoadAssistanceOnline(state) {
    const requestBody = this.transformStateToRequestBody(state);

    this.roadAssistanceService.sendRoadAssistanceOnline(requestBody, state.policySelected)
      .pipe(spinner('send-road-assistance-online'))
      .subscribe(
        (res: any) => {
          state.message = this.getResultMessageByCode(res, state);
          this.updatedStateOnNav(state, RoadAssistanceUrls.MESSAGE);
        },
        error => {
          if (state.assistanceData.needTransportService) {
            this.or.regiesterRoadAssistance(RoadAssistanceOperation.TAXI_SENT_ERROR,
              state.policySelected.frameNumber || null
            );
          }
          state.message = {
            type: RoadAssistanceMessageType.KO,
            topText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.KO_ASIST'),
            bottomText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.OK_ASIST_KO_TRANSP'),
            phoneNumber: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.CONTACT_PHONE')
          };
          this.updatedStateOnNav(state, RoadAssistanceUrls.MESSAGE);
        }
      );
  }

  getResultMessageByCode(response, state: Partial<RoadAssistanceContextState>): RoadAssistanceMessage {
    let message: RoadAssistanceMessage = null;

    if (response.code === 0) {
      message = this.getMessageCode0(response, message);
    } else if (response.code >= 1 && response.code <= 10 || response.code === 11) {
      message = {
        type: RoadAssistanceMessageType.OK,
        topText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.ASSISTANCE_ONGOING'),
        bottomText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.KO_ASIST_OK_CALL_ME'),
      };
    } else if (response.code >= 12 && response.code <= 50) {
      if ((response.code === 17 || response.code === 18) && state.assistanceData.needTransportService) {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.TAXI_SENT,
          state.policySelected.frameNumber || null
        );
      }
      message = {
        type: RoadAssistanceMessageType.OK,
        topText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.ASSISTANCE_ONGOING'),
        bottomText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.OK_ASIST_TRANSP_UNKNOWN_CALL'),
      };
    } else if (response.code >= 51 && response.code <= 100) {
      if (state.assistanceData.needTransportService) {
        this.registerRoadAssistanceTaxiSend(state);
      }
      message = {
        type: RoadAssistanceMessageType.KO,
        topText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.KO_ASIST'),
        bottomText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.OK_ASIST_KO_TRANSP'),
        phoneNumber: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.CONTACT_PHONE')
      };
    } else {
      if (state.assistanceData.needTransportService) {
        this.or.regiesterRoadAssistance(RoadAssistanceOperation.TAXI_SENT_ERROR,
          state.policySelected.frameNumber || null
        );
      }
      message = {
        type: RoadAssistanceMessageType.KO,
        topText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.PROBLEM'),
        bottomText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.KO_ALTITUDE'),
        phoneNumber: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.CONTACT_PHONE')
      };
    }
    return message;
  }

  private getMessageCode0(response: any, message: RoadAssistanceMessage) {
    if (response.desc.includes('altitud')) {
      message = {
        type: RoadAssistanceMessageType.OK,
        topText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.ASSISTANCE_ONGOING'),
        bottomText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.KO_ASIST_OK_CALL_ME'),
      };
    } else {
      message = {
        type: RoadAssistanceMessageType.OK,
        topText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.ASSISTANCE_ONGOING'),
        bottomText: this.translate.instant('ROAD_ASSISTANCE.MESSAGE.OK_ASIST_TRANSP'),
      };
    }
    return message;
  }

  private registerRoadAssistanceTaxiSend(state: Partial<RoadAssistanceContextState>) {
    this.or.regiesterRoadAssistance(RoadAssistanceOperation.TAXI_SENT,
      state.policySelected.frameNumber || null
    );
  }

  saveOpinatorParams(result: 'ok' | 'ko') {
    this.opinatorHelper.saveOpinatorParams({ result }, true);
  }

  async getRoadAssistanceAddressInfo(addressData?: Address): Promise<RoadAssistanceAddress> {
    const addressResume = this.addressHelper.getAddressResumeCustom(addressData);
    const mapsAddress = await this.geoHelperService.geocodeAddressFirstResult(addressResume).toPromise()
      .catch(e => null);

    return {
      addressData: await this.composeAddressData(addressData),
      rawAddress: [`${addressData.streetType} ${addressData.street}`, addressData.number].join(', '),
      fullAddress: this.composeFullAddress(addressData),
      lat: mapsAddress.geometry.location.lat,
      lng: mapsAddress.geometry.location.lng,
      postalCode: addressData.postalCode,
      googleMapResult: mapsAddress
    };
  }

  showCallActionSheet(state: Partial<RoadAssistanceContextState>) {
    this.or.regiesterRoadAssistance(RoadAssistanceOperation.PHONE_CALL,
      state.policySelected.frameNumber || null
    );
    this.urlHelper.openPhone(this.translate.instant('ROAD_ASSISTANCE.MESSAGE.CONTACT_PHONE'));
  }

  async composeAddressData(addressData?: Address) {
    const streetType = await this.getAddressStreetType(addressData)
      .catch(() => null);

    if (addressData.street !== '') {
      addressData.streetType = capitalize(addressData?.streetType) || capitalize(streetType?.description) || '';
      addressData.street = addressData?.streetType !== ''
        ? addressData?.street?.replace(`${addressData?.streetType} `, '')
        : addressData.street;
    }

    return addressData;
  }

  private async getAddressStreetType(addressData: Address) {
    const streetTypes = await this.addressHelper.getStreetTypes()
      .pipe(spinner('get-street-types'))
      .toPromise()
      .catch(() => []);

    const streetType = streetTypes.find(s => {
      return addressData?.street?.toLowerCase().includes(s?.description?.toLowerCase());
    });

    return streetType || '';
  }

  composeFullAddress(addr: Address) {
    if (!addr) {
      return '';
    }
    const addressComponents = [
      `${addr.streetType} ${addr.street}`,
      addr.number,
      addr.stair,
      addr.floor,
      addr.door,
      addr.postalCode,
      addr.city ? addr.city : addr.locality,
      addr.province,
      addr.countryLongName ? addr.countryLongName : null,
      addr.additionalInfo ? addr.additionalInfo : null
    ];
    return addressComponents
      .filter(component => !!component)
      .join(', ');
  }

  async vehicleValid(state: Partial<RoadAssistanceContextState>): Promise<boolean> {
    const body = {
      plate: state.policySelected.frameNumber,
      policyId: state.policySelected.policyNumber,
      asistanceDate: moment().format('YYYY-MM-DD'),
      cause: '1'
    };

    const vehicleValid = await this.roadAssistanceService.validateProceedings(body, state.policySelected)
      .pipe(spinner('validate-proceedings'))
      .toPromise()
      .catch(e => { this.initializeRoadAssistanceOfflineFlow(state); return null; });

    return !this.checkInvalidDesciptions(vehicleValid);
  }

  checkInvalidDesciptions(response): boolean {
    const invalidDesciptions =
      new RegExp(`${RoadAssistanceValidationStrings.PENDING_RECEIPTS}|${RoadAssistanceValidationStrings.NOT_VALID_VEHICLE}`);
    const invalidDesciptionsCod = [RoadAssistanceValidationCodes.PENDING_RECEIPTS,
      RoadAssistanceValidationCodes.NOT_VALID_VEHICLE];
    const availableLimitCode = this.mmobileHelper.getCustomConfigData('ROAD_ASSISTANCE', 'availableLimitCode');

    if (availableLimitCode) {
      return invalidDesciptionsCod.includes(response.code);
    } else {
      return invalidDesciptions.test(response.desc);
    }
  }

  isOnlineCase(state) {
    const onlineCaseCodes = [1, 33];
    return onlineCaseCodes.includes(state?.problemSelected?.code);
  }

  private navToSelectProblem(state: Partial<RoadAssistanceContextState>) {
    const resetState: Partial<RoadAssistanceContextState> = {};
    resetState.policySelected = state.policySelected;
    resetState.fromPolicyDetail = state.fromPolicyDetail;
    return this.updatedStateOnNav(resetState, RoadAssistanceUrls.SELECT_PROBLEM);
  }

  navToSelectPolicy(state: Partial<RoadAssistanceContextState>) {
    const resetState: Partial<RoadAssistanceContextState> = {};
    resetState.fromPolicyDetail = state.fromPolicyDetail;
    return this.updatedStateOnNav(resetState, RoadAssistanceUrls.SELECT_POLICY);
  }

  private navToMapFromProblemType(state: Partial<RoadAssistanceContextState>) {
    this.updatedStateOnNav(state, RoadAssistanceUrls.MAP, {
      progressBarPercentageDecimal: 0.5,
      showSendButton: true
    });
  }

  public navToUndergroundParking(state: Partial<RoadAssistanceContextState>) {
    return this.updatedStateOnNav(state, RoadAssistanceUrls.UNDERGROUND_PARKING, {
      progressBarPercentageDecimal: 0.6,
    });
  }

  private navToContactDataFromProblemType(state: Partial<RoadAssistanceContextState>) {
    this.updatedStateOnNav(state, RoadAssistanceUrls.CONTACT_DATA, {
      progressBarPercentageDecimal: 0.5
    });
  }

  private navToContactData(state: Partial<RoadAssistanceContextState>) {
    return this.updatedStateOnNav(state, RoadAssistanceUrls.CONTACT_DATA, {
      progressBarPercentageDecimal: 0.8,
      showAddessInput: true
    });
  }

  private navToAdditionalComment(state: Partial<RoadAssistanceContextState>) {
    return this.updatedStateOnNav(state, RoadAssistanceUrls.ADDITIONAL_COMMENT, {
      progressBarPercentageDecimal: 0.9
    });
  }

  private navToContactDataBackMap(state: Partial<RoadAssistanceContextState>) {
    return this.updatedStateOnNav(state, RoadAssistanceUrls.CONTACT_DATA, {
      progressBarPercentageDecimal: 0.8,
    });
  }

  private navToMapBackContactData(state: Partial<RoadAssistanceContextState>) {
    return this.updatedStateOnNav(state, RoadAssistanceUrls.MAP, {
      progressBarPercentageDecimal: 0.5,
      showSendButton: true
    });
  }

  private navToMap(state: Partial<RoadAssistanceContextState>) {
    return this.updatedStateOnNav(state, RoadAssistanceUrls.MAP, {
      progressBarPercentageDecimal: 0.8,
      hideCallButton: true
    });
  }

  private navFromMapOnBackWithOccupants(state: Partial<RoadAssistanceContextState>) {
    if (state.assistanceData?.needTransportService) {
      return this.updatedStateOnNav(state, RoadAssistanceUrls.TRANSPORT_SERVICE, {
        progressBarPercentageDecimal: 0.75,
      });
    } else {
      return this.updatedStateOnNav(state, RoadAssistanceUrls.NUM_TRAVELERS, {
        progressBarPercentageDecimal: 0.7,
      });
    }
  }

  private navFromTravelersCheck(state: Partial<RoadAssistanceContextState>) {
    if (state.assistanceData.numberOfOccupants < 2) {
      return this.updatedStateOnNav(state, RoadAssistanceUrls.MAP, {
        progressBarPercentageDecimal: 0.8,
        hideCallButton: true
      });
    } else {
      return this.updatedStateOnNav(state, RoadAssistanceUrls.TRANSPORT_SERVICE, {
        progressBarPercentageDecimal: 0.75,
      });
    }
  }

  private navToInfoPage(state: Partial<RoadAssistanceContextState>) {
    return this.updatedStateOnNav(state, RoadAssistanceUrls.INFO, {
      progressBarPercentageDecimal: 0.5
    });
  }
}
